import { useState, useRef, useEffect, useCallback, useId } from "react";
import { searchMentionUsers } from "api/community";
import { detectMentionQuery } from "utils/mention";
import {
  parseTokensToDisplay,
  serializeDisplayToTokens,
  computeChange,
  adjustEntities,
  insertMentionDisplay,
} from "utils/mentionEditor";

const DEBOUNCE_MS = 300;
const MIN_QUERY = 2; // @ 뒤 최소 글자 수 — 백엔드 users/search 가 2자 미만은 400 반환
const BACKOFF_MS = 2000; // rate limit 시 조회 중단 시간
const BLUR_CLOSE_MS = 120; // 드롭다운 항목 mousedown이 blur보다 먼저 처리되도록 주는 유예

// textarea 하나의 @멘션 자동완성 상태를 캡슐화한다.
export default function useMentionAutocomplete({ value, setValue, textareaRef, maxLength }) {
  const [state, setState] = useState({
    open: false,
    results: [],
    loading: false,
    activeIndex: 0,
  });
  const listId = useId();
  const rangeRef = useRef(null);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);
  const backoffUntilRef = useRef(0);
  const pendingCaretRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  const entitiesRef = useRef([]);
  const prevValueRef = useRef(value);

  const close = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    reqIdRef.current += 1; // in-flight 요청을 stale 처리해 늦게 온 응답으로 재오픈되지 않게 함
    rangeRef.current = null;
    setState({ open: false, results: [], loading: false, activeIndex: 0 });
  }, []);

  const handleBlur = useCallback(() => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    // 드롭다운 항목의 onMouseDown(preventDefault + 선택)이 먼저 처리되도록 살짝 지연 후 닫는다.
    blurTimeoutRef.current = setTimeout(() => {
      close();
    }, BLUR_CLOSE_MS);
  }, [close]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    },
    []
  );

  // 선택 후 caret 복원 (제어 컴포넌트라 value 반영 뒤 실행)
  useEffect(() => {
    if (pendingCaretRef.current == null || !textareaRef.current) return;
    const pos = pendingCaretRef.current;
    pendingCaretRef.current = null;
    const el = textareaRef.current;
    el.focus();
    try {
      el.setSelectionRange(pos, pos);
    } catch (_e) {
      /* noop */
    }
  }, [value, textareaRef]);

  // 외부에서 value가 바뀐 경우(제출 후 "" 초기화 등) 엔티티를 자동 동기화한다.
  // handleChange/selectUser/initFromRaw 경로는 prevValueRef를 직접 갱신하므로 여기선 no-op.
  useEffect(() => {
    if (prevValueRef.current === value) return;
    entitiesRef.current = adjustEntities(
      entitiesRef.current,
      computeChange(prevValueRef.current, value)
    );
    prevValueRef.current = value;
  }, [value]);

  const runSearch = useCallback((query) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setState((s) => ({ ...s, open: true, loading: true }));
    debounceRef.current = setTimeout(async () => {
      if (backoffUntilRef.current && Date.now() < backoffUntilRef.current) {
        // backoff 중에는 조용히 닫는다("검색 결과가 없어요" 노출 금지).
        close();
        return;
      }
      const reqId = ++reqIdRef.current;
      const res = await searchMentionUsers(query);
      if (reqId !== reqIdRef.current) return; // stale
      if (res.rateLimited) {
        backoffUntilRef.current = Date.now() + BACKOFF_MS;
        close();
        return;
      }
      setState({ open: true, results: res.users, loading: false, activeIndex: 0 });
    }, DEBOUNCE_MS);
  }, [close]);

  const detect = useCallback(
    (text, caret) => {
      const ctx = detectMentionQuery(text, caret);
      if (!ctx || ctx.query.length < MIN_QUERY) {
        close();
        return;
      }
      rangeRef.current = { start: ctx.start, end: ctx.end };
      runSearch(ctx.query);
    },
    [close, runSearch]
  );

  const handleChange = useCallback(
    (e) => {
      const v = maxLength ? e.target.value.slice(0, maxLength) : e.target.value;
      const caret = e.target.selectionStart;
      entitiesRef.current = adjustEntities(entitiesRef.current, computeChange(prevValueRef.current, v));
      prevValueRef.current = v;
      setValue(v);
      detect(v, caret);
    },
    [detect, setValue, maxLength]
  );

  const selectUser = useCallback(
    (user) => {
      if (!rangeRef.current) return;
      const { text, entities, caret } = insertMentionDisplay(
        value,
        entitiesRef.current,
        rangeRef.current,
        user
      );
      // caret == "before + @닉네임 " 직후 위치. 표시 텍스트가 maxLength를 넘으면 삽입 취소.
      if (maxLength && caret > maxLength) {
        close();
        return;
      }
      const clipped = maxLength ? text.slice(0, maxLength) : text;
      // 잘려나간 뒤쪽 범위에 걸친 엔티티는 제거(방어)
      entitiesRef.current = entities.filter((en) => en.end <= clipped.length);
      prevValueRef.current = clipped;
      pendingCaretRef.current = Math.min(caret, clipped.length);
      setValue(clipped);
      close();
    },
    [value, setValue, maxLength, close]
  );

  // 제출용 직렬화 — ref 기반이라 identity가 안정적이다.
  const serialize = useCallback(
    () => serializeDisplayToTokens(prevValueRef.current, entitiesRef.current),
    []
  );

  // 토큰 원문으로 초기화 (수정 모드 진입용)
  const initFromRaw = useCallback(
    (raw) => {
      const { text, entities } = parseTokensToDisplay(raw || "");
      entitiesRef.current = entities;
      prevValueRef.current = text;
      setValue(text);
    },
    [setValue]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (!state.open) return;
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return;
      }
      const n = state.results.length;
      if (n === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setState((s) => ({ ...s, activeIndex: (s.activeIndex + 1) % n }));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setState((s) => ({ ...s, activeIndex: (s.activeIndex - 1 + n) % n }));
      } else if (e.key === "Enter") {
        e.preventDefault();
        selectUser(state.results[state.activeIndex]);
      }
    },
    [state, selectUser, close]
  );

  return {
    open: state.open,
    results: state.results,
    loading: state.loading,
    activeIndex: state.activeIndex,
    listId,
    activeOptionId: state.results.length > 0 ? `${listId}-opt-${state.activeIndex}` : undefined,
    handleChange,
    handleKeyDown,
    handleBlur,
    selectUser,
    close,
    serialize,
    initFromRaw,
  };
}
